import { TRANSFER_STATUS_LABELS } from "@/modules/inventory/presentation/ui/transfers/TransferStatusBadge";
import { transferItemTypeLabel } from "@/modules/inventory/presentation/ui/transfers/transferLaoLabels";
import type { TransferReportByBranchRow } from "@/modules/reports/domain/repo/transfer/get-transfer-report-by-branch";
import type { TransferReportByCategoryRow } from "@/modules/reports/domain/repo/transfer/get-transfer-report-by-category";
import type { TransferReportByItemRow } from "@/modules/reports/domain/repo/transfer/get-transfer-report-by-item";
import type { TransferReportLineRow } from "@/modules/reports/domain/repo/transfer/get-transfer-report-lines";

function csvCell(value: unknown) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function csvRow(cells: unknown[]) {
  return cells.map(csvCell).join(",");
}

export function downloadTransferReportCsv(payload: {
  byBranch: TransferReportByBranchRow[];
  byCategory?: TransferReportByCategoryRow[];
  byItem: TransferReportByItemRow[];
  lines: TransferReportLineRow[];
  linesTotal: number;
  filename?: string;
}) {
  const { byBranch, byCategory = [], byItem, lines, linesTotal } = payload;

  const branchHeaders = [
    "ລະຫັດສາຂາ",
    "ຊື່ສາຂາ",
    "ຄຳຂໍ",
    "ບັນທັດ",
    "ຂໍ",
    "ສົ່ງ",
    "ຮັບ",
    "ຄວາມຕ່າງ",
    "ມູນຄ່າສົ່ງ (₭)",
  ];
  const branchRows = byBranch.map((row) =>
    csvRow([
      row.branchCode,
      row.branchName,
      row.requestCount,
      row.lineCount,
      row.qtyRequested,
      row.qtyShipped,
      row.qtyReceived,
      row.qtyVariance,
      row.shippedValueLak,
    ]),
  );

  const categoryHeaders = [
    "ຫມວດ",
    "ຄຳຂໍ",
    "ບັນທັດ",
    "ຂໍ",
    "ສົ່ງ",
    "ຮັບ",
    "ຄວາມຕ່າງ",
    "ມູນຄ່າສົ່ງ (₭)",
  ];
  const categoryRows = byCategory.map((row) =>
    csvRow([
      row.categoryName,
      row.requestCount,
      row.lineCount,
      row.qtyRequested,
      row.qtyShipped,
      row.qtyReceived,
      row.qtyVariance,
      row.shippedValueLak,
    ]),
  );

  const itemHeaders = [
    "ລາຍການ",
    "ປະເພດ",
    "ຫນ່ວຍ",
    "ຄຳຂໍ",
    "ບັນທັດ",
    "ຂໍ",
    "ສົ່ງ",
    "ຮັບ",
    "ຄວາມຕ່າງ",
    "ມູນຄ່າສົ່ງ (₭)",
  ];
  const itemRows = byItem.map((row) =>
    csvRow([
      row.itemName,
      transferItemTypeLabel(row.itemType),
      row.unit,
      row.requestCount,
      row.lineCount,
      row.qtyRequested,
      row.qtyShipped,
      row.qtyReceived,
      row.qtyVariance,
      row.shippedValueLak,
    ]),
  );

  const lineHeaders = [
    "ເລກທີ",
    "ສະຖານະ",
    "ສາຂາ",
    "ບັນທັດ",
    "ລາຍການ",
    "ປະເພດ",
    "ຂໍ",
    "ອະນຸມັດ",
    "ສົ່ງ",
    "ຮັບ",
    "ຄວາມຕ່າງ",
    "ຫນ່ວຍ",
    "ວັນທີສົ່ງຄຳຂໍ",
    "ວັນທີຈັດສົ່ງ",
    "ວັນທີຮັບ",
  ];
  const lineRows = lines.map((r) =>
    csvRow([
      r.requestNo,
      TRANSFER_STATUS_LABELS[r.status],
      `${r.fromBranchCode} ${r.fromBranchName}`,
      r.lineNo,
      r.itemName,
      transferItemTypeLabel(r.itemType),
      r.qtyRequested,
      r.qtyApproved ?? "",
      r.qtyShipped ?? "",
      r.qtyReceived ?? "",
      r.qtyVariance,
      r.unit,
      r.submittedAt ?? "",
      r.shippedAt ?? "",
      r.receivedAt ?? "",
    ]),
  );

  const csvContent = [
    "=== ສະຫຼຸບຕາມສາຂາ ===",
    branchHeaders.join(","),
    ...branchRows,
    "",
    "=== ສະຫຼຸບຕາມຫມວດວັດຖຸດິບ ===",
    categoryHeaders.join(","),
    ...categoryRows,
    "",
    "=== ສະຫຼຸບຕາມສິນຄ້າ ===",
    itemHeaders.join(","),
    ...itemRows,
    "",
    `=== ລາຍລະອຽດບັນທັດ (${lines.length.toLocaleString("lo-LA")} / ${linesTotal.toLocaleString("lo-LA")}) ===`,
    lineHeaders.join(","),
    ...lineRows,
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    payload.filename ??
    `transfer-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** @deprecated ใช้ downloadTransferReportCsv แทน */
export function downloadTransferReportLinesCsv(
  lines: TransferReportLineRow[],
  filename?: string,
) {
  downloadTransferReportCsv({
    byBranch: [],
    byItem: [],
    lines,
    linesTotal: lines.length,
    filename,
  });
}
